import type { CSSProperties } from 'react';
import { motion } from '../../design/motion';

export type MapLayerMode = 'presence' | 'sceaux' | 'constellation';

interface MapLayersProps {
  mapMode: MapLayerMode;
  setMapMode: (mode: MapLayerMode) => void;
  showThreads: boolean;
  setShowThreads: (value: boolean) => void;
  showTemporalOnly: boolean;
  setShowTemporalOnly: (value: boolean) => void;
  temporalUnlocked: boolean;
  runsLength: number;
  showSegments: boolean;
  setShowSegments: (value: boolean) => void;
  showInscriptionsLayer: boolean;
  setShowInscriptionsLayer: (value: boolean) => void;
  segmentsLabel: string;
  inscriptionsLabel: string;
  presenceTabLabel: string;
  sceauxTabLabel: string;
  constellationTabLabel: string;
  presenceHint: string;
  sceauxHint: string;
  constellationHint: string;
  sceauxSealedLabel: string;
  sceauxSealedEmpty: string;
  sceauxDraftsLabel: string;
}

export function MapLayers({
  mapMode,
  setMapMode,
  showThreads,
  setShowThreads,
  showTemporalOnly,
  setShowTemporalOnly,
  temporalUnlocked,
  runsLength,
  showSegments,
  setShowSegments,
  showInscriptionsLayer,
  setShowInscriptionsLayer,
  segmentsLabel,
  inscriptionsLabel,
  presenceTabLabel,
  sceauxTabLabel,
  constellationTabLabel,
  presenceHint,
  sceauxHint,
  constellationHint,
  sceauxSealedLabel,
  sceauxSealedEmpty,
  sceauxDraftsLabel,
}: MapLayersProps) {
  const hintByMode: Record<MapLayerMode, string> = {
    presence: presenceHint,
    sceaux: sceauxHint,
    constellation: constellationHint,
  };
  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {(['presence', 'sceaux', 'constellation'] as const).map((mode) => {
          const labels: Record<MapLayerMode, string> = {
            presence: presenceTabLabel,
            sceaux: sceauxTabLabel,
            constellation: constellationTabLabel,
          };
          const isActive = mapMode === mode;
          return (
            <button
              key={mode}
              type="button"
              onClick={() => setMapMode(mode)}
              style={{
                padding: '8px 16px',
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#003D2C',
                background: isActive ? 'rgba(0,61,44,0.10)' : 'transparent',
                border: `1px solid ${isActive ? 'rgba(0,61,44,0.30)' : 'rgba(0,61,44,0.16)'}`,
                borderRadius: 999,
                cursor: 'pointer',
                opacity: isActive ? 0.96 : 0.68,
                transform: `translateY(0) scale(${isActive ? 1 : 0.99})`,
                transition: motion.transition([
                  { property: 'opacity', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
                  { property: 'transform', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
                  { property: 'filter', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
                ]),
              }}
            >
              {labels[mode]}
            </button>
          );
        })}
      </div>
      <p
        style={{
          margin: 0,
          marginBottom: 12,
          fontFamily: 'var(--font-serif)',
          fontSize: 12,
          fontStyle: 'italic',
          color: '#003D2C',
          opacity: 0.5,
          textAlign: 'center',
          letterSpacing: '0.01em',
        }}
        aria-live="polite"
      >
        {hintByMode[mapMode]}
      </p>

      {mapMode === 'sceaux' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, alignItems: 'center' }}>
          <div style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#003D2C',
            opacity: 0.6,
          }}>
            {sceauxSealedLabel}
          </div>
          <p style={{
            margin: 0,
            fontFamily: 'var(--font-serif)',
            fontSize: 12,
            fontStyle: 'italic',
            color: '#8E8982',
            opacity: 0.7,
          }}>
            {sceauxSealedEmpty}
          </p>
          <div style={{
            width: '80%',
            maxWidth: 300,
            height: 1,
            background: 'rgba(0,61,44,0.08)',
            margin: '6px 0',
          }} />
          <div style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#6B6455',
            opacity: 0.5,
          }}>
            {sceauxDraftsLabel}
          </div>
        </div>
      )}

      {mapMode === 'sceaux' && (
        (() => {
          const visuallyHidden: CSSProperties = {
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          };
          const pillBase: CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 32,
            padding: '0 12px',
            border: '1px solid #DBD4C6',
            background: '#FAF8F3',
            color: '#1F3B2E',
            borderRadius: 999,
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            opacity: 0.9,
            transform: 'translateY(0)',
            transition: motion.transition([
              { property: 'opacity', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
              { property: 'transform', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
              { property: 'filter', durationMs: motion.t('brisk'), easing: motion.ease('appear') },
            ])
          };
          const pillChecked: CSSProperties = {
            background: 'rgba(31,59,46,0.06)',
            borderColor: 'rgba(31,59,46,0.35)'
          };
          const dotBase: CSSProperties = {
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0
          };

          const PillToggle = ({
            checked,
            onChange,
            label,
            ariaLabel
          }: { checked: boolean; onChange: () => void; label: string; ariaLabel: string }) => (
            <label
              style={{
                ...pillBase,
                ...(checked ? pillChecked : {}),
                outline: 'none'
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                style={visuallyHidden}
                aria-label={ariaLabel}
                onFocus={(e) => {
                  e.currentTarget.parentElement!.style.outline = '2px solid rgba(31,59,46,0.25)';
                  e.currentTarget.parentElement!.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.currentTarget.parentElement!.style.outline = 'none';
                  e.currentTarget.parentElement!.style.outlineOffset = '0';
                }}
              />
              <span
                style={{
                  ...dotBase,
                  background: checked ? 'rgba(31,59,46,0.55)' : 'transparent',
                  border: `1px solid ${checked ? 'rgba(31,59,46,0.55)' : '#DBD4C6'}`
                }}
              />
              <span>{label}</span>
            </label>
          );

          return (
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {(runsLength > 0 || temporalUnlocked) && (
                <>
                  <PillToggle
                    checked={showThreads}
                    onChange={() => setShowThreads(!showThreads)}
                    label="Threads"
                    ariaLabel="Threads"
                  />
                  {temporalUnlocked && (
                    <PillToggle
                      checked={showTemporalOnly}
                      onChange={() => setShowTemporalOnly(!showTemporalOnly)}
                      label="Temporal Meridians only"
                      ariaLabel="Temporal Meridians only"
                    />
                  )}
                </>
              )}
              <PillToggle
                checked={showSegments}
                onChange={() => setShowSegments(!showSegments)}
                label={segmentsLabel}
                ariaLabel={segmentsLabel}
              />
              <PillToggle
                checked={showInscriptionsLayer}
                onChange={() => setShowInscriptionsLayer(!showInscriptionsLayer)}
                label={inscriptionsLabel}
                ariaLabel={inscriptionsLabel}
              />
            </div>
          );
        })()
      )}

      {mapMode === 'constellation' && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, justifyContent: 'center', fontSize: 10, fontFamily: 'var(--font-sans)', color: '#6B6455' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#e5e5e5', border: '1px solid #ccc' }} />
            Inexploré
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#d4af37' }} />
            Entré
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#4a7c59' }} />
            Scellé
          </span>
        </div>
      )}
    </>
  );
}
